import { For, Show, createMemo, createSignal, type Component, type JSX } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { usePanel } from "@/shared/use-panel";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";
import { PanelChrome } from "../console/ui";
import {
  PanelBadge,
  PanelDetail,
  PanelKpi,
  PanelKpiGrid,
  type DetailSection,
  type Intent,
} from "@/shared/panel-primitives";

interface ActionPolicy {
  canDisable: boolean;
  canEnable: boolean;
  canDelete: boolean;
  reason?: string | null;
}

interface SignatureInfo {
  status: string;
  signer?: string | null;
  isMicrosoft: boolean;
}

interface AutorunRow {
  id: string;
  category: string;
  name: string;
  location: string;
  entryType: string;
  command?: string | null;
  imagePath?: string | null;
  arguments?: string | null;
  enabled: boolean;
  signature?: SignatureInfo | null;
  sha256?: string | null;
  file?: AutorunDetail["file"] | null;
  hashes?: AutorunDetail["hashes"] | null;
  actionPolicy: ActionPolicy;
  fingerprint: string;
}

interface AutorunDetail extends AutorunRow {
  arguments?: string | null;
  file?: {
    exists: boolean;
    sizeBytes?: number | null;
    modifiedAtUtc?: string | null;
    companyName?: string | null;
    productName?: string | null;
    fileVersion?: string | null;
  } | null;
  hashes?: { sha256?: string | null } | null;
}

interface ScanResult {
  count: number;
  rows: AutorunRow[];
  warnings: string[];
  truncated: boolean;
}

interface GetResult {
  entry: AutorunDetail;
}

interface VtResult {
  sha256: string;
  status: string;
  malicious?: number | null;
  suspicious?: number | null;
  harmless?: number | null;
  undetected?: number | null;
  permalink?: string | null;
  error?: string | null;
}

interface VtLookupResult {
  results: VtResult[];
  warnings: string[];
}

const CATEGORIES = [
  ["all", "Toutes"],
  ["logon", "Logon"],
  ["startup", "Startup"],
  ["scheduled_tasks", "Tâches"],
  ["services", "Services"],
  ["drivers", "Drivers"],
  ["winlogon", "Winlogon"],
  ["ifeo", "IFEO"],
  ["explorer_ie_addons", "Explorer/IE"],
  ["winsock_providers", "Winsock"],
  ["codecs", "Codecs"],
  ["print_monitors", "Print"],
  ["lsa_providers", "LSA"],
  ["known_dlls", "KnownDLLs"],
] as const;

function sigIntent(sig: SignatureInfo | null | undefined): Intent {
  if (!sig) return "muted";
  if (sig.status === "valid" && sig.isMicrosoft) return "ok";
  if (sig.status === "valid") return "info";
  if (sig.status === "missing") return "muted";
  return "warn";
}

function vtIntent(status: string): Intent {
  if (status === "clean") return "ok";
  if (status === "detected") return "danger";
  if (status === "unknown") return "muted";
  return "warn";
}

function vtLabel(vt: VtResult | undefined): string {
  if (!vt) return "n/a";
  if (vt.status === "detected")
    return `${vt.malicious ?? 0}/${(vt.malicious ?? 0) + (vt.suspicious ?? 0) + (vt.harmless ?? 0) + (vt.undetected ?? 0)}`;
  return vt.status;
}

function shortPath(path: string | null | undefined): string {
  if (!path) return "";
  return path.length > 72 ? `…${path.slice(-69)}` : path;
}

function formatBytes(value: number | null | undefined): string {
  if (value == null) return "";
  if (value > 1024 * 1024 * 1024) return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (value > 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} MB`;
  if (value > 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${value} B`;
}

const AutorunsPanel: Component = () => {
  const ws = useWs();
  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const [category, setCategory] = createSignal("all");
  const [query, setQuery] = createSignal("");
  const [hideMicrosoft, setHideMicrosoft] = createSignal(true);
  const [onlyUnsigned, setOnlyUnsigned] = createSignal(false);
  const [includeDisabled, setIncludeDisabled] = createSignal(false);
  const [selected, setSelected] = createSignal<AutorunRow | null>(null);
  const [vtResults, setVtResults] = createSignal<Record<string, VtResult>>({});

  const scanQuery = createToolQuery<ScanResult>(() => ({
    tool: "autoruns_scan",
    params: {
      categories: category() === "all" ? [] : [category()],
      query: query().trim() || undefined,
      hideMicrosoft: hideMicrosoft(),
      onlyUnsigned: onlyUnsigned(),
      includeDisabled: includeDisabled(),
      verifySignatures: true,
      includeHashes: false,
      limit: 1000,
      offset: 0,
    },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const detailQuery = createToolQuery<GetResult>(() => ({
    tool: "autoruns_get",
    params: { id: selected()?.id ?? "", includeRaw: false },
    enabled: enabled() && !!selected(),
    refetchInterval: 0,
  }));

  const panel = usePanel(
    () => ({ isFetching: scanQuery.isFetching, error: scanQuery.error }),
    () => ({ isFetching: detailQuery.isFetching, error: detailQuery.error }),
  );

  const rows = createMemo(() => scanQuery.data?.rows ?? []);
  const detail = createMemo(() => detailQuery.data?.entry ?? selected());

  const kpis = createMemo(() => {
    let disabled = 0;
    let unsigned = 0;
    let nonMicrosoft = 0;
    for (const row of rows()) {
      if (!row.enabled) disabled += 1;
      if (row.signature?.status !== "valid") unsigned += 1;
      if (row.signature?.isMicrosoft !== true) nonMicrosoft += 1;
    }
    return { total: scanQuery.data?.count ?? rows().length, disabled, unsigned, nonMicrosoft };
  });

  async function runAction(row: AutorunRow, action: "disable" | "enable" | "delete") {
    const label = action === "disable" ? "Désactiver" : action === "enable" ? "Réactiver" : "Supprimer";
    const ok = await panel.confirm.confirm({
      title: `${label} — ${row.name}`,
      body: `Emplacement: ${row.location}\nImage: ${row.imagePath || row.command || "n/a"}`,
      level: action === "delete" ? "danger" : "warn",
      confirmLabel: label,
      entityName: action === "delete" ? row.name : undefined,
    });
    if (!ok) return;

    panel.setActionStatus({ text: `${label} en cours...`, level: "info" });
    try {
      await panel.tools.call(
        "autoruns_action",
        { id: row.id, action, expectedFingerprint: row.fingerprint },
        { userConfirmedAt: new Date().toISOString(), timeoutMs: 90_000 },
      );
      panel.setActionStatus({ text: `${label} terminé.`, level: "info" });
      await scanQuery.refetch();
      if (selected()?.id === row.id) await detailQuery.refetch();
      setTimeout(() => panel.setActionStatus(null), 2500);
    } catch (err) {
      panel.setActionStatus({ text: `Erreur: ${err instanceof Error ? err.message : String(err)}`, level: "danger" });
    }
  }

  async function lookupVt(row: AutorunRow) {
    panel.setActionStatus({ text: "VirusTotal...", level: "info" });
    try {
      const result = await panel.tools.call<VtLookupResult>(
        "autoruns_vt_lookup",
        { ids: [row.id] },
        { userConfirmedAt: new Date().toISOString(), timeoutMs: 90_000 },
      );
      const first = result.results[0];
      if (first) setVtResults((prev) => ({ ...prev, [row.id]: first }));
      panel.setActionStatus(null);
    } catch (err) {
      panel.setActionStatus({ text: `VT: ${err instanceof Error ? err.message : String(err)}`, level: "danger" });
    }
  }

  const columns: Column<AutorunRow>[] = [
    {
      accessorKey: "enabled",
      header: "Etat",
      cell: (info) => (
        <PanelBadge intent={(info.getValue() as boolean) ? "ok" : "muted"}>
          {(info.getValue() as boolean) ? "actif" : "off"}
        </PanelBadge>
      ),
    },
    { accessorKey: "category", header: "Catégorie" },
    { accessorKey: "name", header: "Nom" },
    {
      accessorKey: "imagePath",
      header: "Image",
      cell: (info) => {
        const value = String(info.getValue() ?? "");
        return <code title={value}>{shortPath(value)}</code>;
      },
    },
    {
      accessorKey: "signature",
      header: "Signature",
      cell: (info) => {
        const sig = info.getValue() as SignatureInfo | null | undefined;
        return <PanelBadge intent={sigIntent(sig)}>{sig?.isMicrosoft ? "Microsoft" : sig?.status || "n/a"}</PanelBadge>;
      },
    },
    {
      accessorKey: "id",
      header: "VT",
      cell: (info) => {
        const row = info.row.original;
        const vt = vtResults()[row.id];
        return <PanelBadge intent={vt ? vtIntent(vt.status) : "muted"}>{vtLabel(vt)}</PanelBadge>;
      },
    },
    {
      accessorKey: "location",
      header: "Emplacement",
      cell: (info) => {
        const value = String(info.getValue() ?? "");
        return <span title={value}>{shortPath(value)}</span>;
      },
    },
  ];

  const toolbar = (
    <>
      <input
        class="panel-filter-input"
        placeholder="Chercher nom, chemin, commande..."
        value={query()}
        onInput={(e) => setQuery(e.currentTarget.value)}
      />
      <select class="panel-select" value={category()} onChange={(e) => setCategory(e.currentTarget.value)}>
        <For each={CATEGORIES}>{([id, label]) => <option value={id}>{label}</option>}</For>
      </select>
      <label class="panel-check">
        <input type="checkbox" checked={hideMicrosoft()} onChange={(e) => setHideMicrosoft(e.currentTarget.checked)} />
        Microsoft masqués
      </label>
      <label class="panel-check">
        <input type="checkbox" checked={onlyUnsigned()} onChange={(e) => setOnlyUnsigned(e.currentTarget.checked)} />
        Non signés
      </label>
      <label class="panel-check">
        <input
          type="checkbox"
          checked={includeDisabled()}
          onChange={(e) => setIncludeDisabled(e.currentTarget.checked)}
        />
        Désactivés
      </label>
      <button class="btn btn--xs btn--ghost" onClick={() => scanQuery.refetch()} title="Rafraîchir">
        ↺ Rafraîchir
      </button>
    </>
  );

  const detailSections = createMemo<DetailSection[]>(() => {
    const d = detail();
    if (!d) return [];
    const vt = vtResults()[d.id];
    return [
      {
        label: "Entrée",
        items: [
          ["Catégorie", d.category],
          ["Type", d.entryType],
          ["Etat", d.enabled ? "Actif" : "Désactivé"],
          ["Emplacement", <code>{d.location}</code>],
          ["Commande", <code>{d.command || ""}</code>],
          ["Arguments", <code>{d.arguments || ""}</code>],
        ],
      },
      {
        label: "Fichier",
        items: [
          ["Image", <code>{d.imagePath || ""}</code>],
          ["Existe", d.file?.exists ? "oui" : "non"],
          ["Taille", formatBytes(d.file?.sizeBytes)],
          ["Version", d.file?.fileVersion || ""],
          ["SHA-256", <code>{d.hashes?.sha256 || d.sha256 || ""}</code>],
        ],
      },
      {
        label: "Réputation",
        items: [
          [
            "Signature",
            <PanelBadge intent={sigIntent(d.signature)}>
              {d.signature?.signer || d.signature?.status || "n/a"}
            </PanelBadge>,
          ],
          [
            "VirusTotal",
            vt ? (
              vt.permalink ? (
                <a href={vt.permalink} target="_blank" rel="noopener noreferrer">
                  <PanelBadge intent={vtIntent(vt.status)}>{vtLabel(vt)}</PanelBadge>
                </a>
              ) : (
                <PanelBadge intent={vtIntent(vt.status)}>{vtLabel(vt)}</PanelBadge>
              )
            ) : (
              "Non consulté"
            ),
          ],
        ],
      },
    ];
  });

  const detailActions = () => {
    const d = detail();
    if (!d) return null;
    const policy = d.actionPolicy;
    return (
      <>
        <button class="btn btn--xs btn--ghost" disabled={!policy.canDisable} onClick={() => runAction(d, "disable")}>
          Désactiver
        </button>
        <button class="btn btn--xs btn--ghost" disabled={!policy.canEnable} onClick={() => runAction(d, "enable")}>
          Réactiver
        </button>
        <button class="btn btn--xs btn--danger" disabled={!policy.canDelete} onClick={() => runAction(d, "delete")}>
          Supprimer
        </button>
        <button class="btn btn--xs btn--ghost" onClick={() => lookupVt(d)}>
          VT
        </button>
        <Show when={policy.reason}>
          <span class="panel-muted-text" title={policy.reason || ""}>
            {policy.reason}
          </span>
        </Show>
      </>
    );
  };

  return (
    <PanelChrome icon="🛡" title="Autoruns" tagType="core" actions={toolbar}>
      <PanelLayout title="Autoruns" status={panel.status()}>
        <PanelKpiGrid>
          <PanelKpi label="Entrées" value={kpis().total} intent="info" />
          <PanelKpi label="Non Microsoft" value={kpis().nonMicrosoft} intent="warn" />
          <PanelKpi label="Non signées" value={kpis().unsigned} intent="danger" />
          <PanelKpi label="Désactivées" value={kpis().disabled} intent="muted" />
        </PanelKpiGrid>

        <Show when={(scanQuery.data?.warnings ?? []).length > 0}>
          <div class="panel-data-empty" style={{ "margin-bottom": "8px", "text-align": "left" }}>
            <For each={scanQuery.data?.warnings ?? []}>{(w) => <div>{w}</div>}</For>
          </div>
        </Show>

        <div class="panel-split">
          <DataTable<AutorunRow>
            rows={rows()}
            columns={columns}
            emptyMessage={panel.enabled() ? "Aucune entrée autoruns." : "Module natif requis."}
            onRowSelect={(row) => setSelected(row)}
            onRowActivate={(row) => setSelected(row)}
            rowActions={(row): JSX.Element => (
              <>
                <button class="btn btn--xs btn--ghost" onClick={() => setSelected(row)}>
                  Détails
                </button>
                <button
                  class="btn btn--xs btn--ghost"
                  disabled={!row.actionPolicy.canDisable}
                  onClick={() => runAction(row, "disable")}
                  title={row.actionPolicy.reason || "Désactiver"}
                >
                  Off
                </button>
                <button
                  class="btn btn--xs btn--ghost"
                  disabled={!row.actionPolicy.canEnable}
                  onClick={() => runAction(row, "enable")}
                  title={row.actionPolicy.reason || "Réactiver"}
                >
                  On
                </button>
                <button class="btn btn--xs btn--ghost" onClick={() => lookupVt(row)} title="Consulter VirusTotal">
                  VT
                </button>
              </>
            )}
          />

          <Show when={detail()}>
            {(d) => (
              <PanelDetail
                title={d().name}
                onClose={() => setSelected(null)}
                actions={detailActions()}
                sections={detailSections()}
              />
            )}
          </Show>
        </div>
      </PanelLayout>
    </PanelChrome>
  );
};

registerPanel("autoruns", AutorunsPanel);
export default AutorunsPanel;
